import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './tcs-invoices.api';

import type { MarkTcsInvoicePaidRequest } from '@utazzo/shared';

const REGISTER_KEY = ['reports', 'tcs-register'] as const;
const certKey = (invoiceId: string | null) => ['tcs-invoices', 'certificates', invoiceId] as const;

const messageOf = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  return fallback;
};

const useRefresh = () => {
  const qc = useQueryClient();
  return (invoiceId?: string | null) => {
    void qc.invalidateQueries({ queryKey: REGISTER_KEY });
    if (invoiceId) void qc.invalidateQueries({ queryKey: certKey(invoiceId) });
  };
};

/** Set/clear the per-invoice TCS paid flag. */
export const useMarkInvoicePaid = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ invoiceId, body }: { invoiceId: string; body: MarkTcsInvoicePaidRequest }) =>
      api.markInvoicePaid(invoiceId, body),
    onSuccess: (_r, v) => refresh(v.invoiceId),
    onError: (err: unknown) => toast.error(messageOf(err, 'Failed to update the TCS paid status')),
  });
};

export const useInvoiceCertificates = (invoiceId: string | null) =>
  useQuery({
    queryKey: certKey(invoiceId),
    queryFn: () => api.listCertificates(invoiceId as string),
    enabled: Boolean(invoiceId),
  });

/** Upload one certificate file: presign -> PUT -> create row. */
export const useUploadCertificate = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ invoiceId, file }: { invoiceId: string; file: File }) =>
      api.uploadCertificate(invoiceId, file),
    onSuccess: (_d, v) => refresh(v.invoiceId),
  });
};

export const useDeleteCertificate = () => {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ invoiceId, docId }: { invoiceId: string; docId: string }) =>
      api.deleteCertificate(invoiceId, docId),
    onSuccess: (_r, v) => refresh(v.invoiceId),
    onError: (err: unknown) => toast.error(messageOf(err, 'Failed to delete the certificate')),
  });
};
